
import { useState, useEffect } from 'react';
import { useEpisodes } from '../../hooks/useEpisodes';
import { useCategories } from '../../hooks/useCategories';
import { useIndustries } from '../../hooks/useIndustries';
import { Episode } from '../../lib/database';
import DataExportImport from '../../components/admin/DataExportImport';
import SEOHead from '../../components/seo/SEOHead';

interface Subscriber {
  id: number;
  name: string;
  email: string;
  country: string;
  industry: string;
  registrationDate: string;
  lastAccess: string;
  totalViews: number;
  episodeViews: { [key: number]: number };
}

// StatCard component
const StatCard = ({ title, value, icon, bgColor, iconColor }: { 
  title: string; 
  value: number | string; 
  icon: string; 
  bgColor: string; 
  iconColor: string; 
}) => (
  <div className={`rounded-xl ${bgColor} p-6`}>
    <div className="flex items-center">
      <i className={`${icon} ${iconColor} text-2xl mr-4`}></i>
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('subscribers');

  // Use the new hooks for persistent data
  const { episodes, loading: episodesLoading, createEpisode, updateEpisode } = useEpisodes();
  const { categories, createCategory, updateCategory, deleteCategory } = useCategories();
  const { industries, createIndustry, updateIndustry, deleteIndustry } = useIndustries();

  // Mock data for subscribers (this would also use a hook in production)
  const [subscribers] = useState<Subscriber[]>([
    {
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.johnson@email.com',
      country: 'United States',
      industry: 'Technology',
      registrationDate: '2024-01-15',
      lastAccess: '2024-01-20',
      totalViews: 45,
      episodeViews: { 1: 12, 2: 18, 3: 15, 4: 8, 5: 12, 6: 10 }
    },
    {
      id: 2,
      name: 'Michael Chen',
      email: 'michael.chen@email.com',
      country: 'Canada',
      industry: 'Finance',
      registrationDate: '2024-01-10',
      lastAccess: '2024-01-19',
      totalViews: 32,
      episodeViews: { 1: 8, 2: 12, 3: 12, 4: 5, 5: 8, 6: 7 }
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      email: 'emily.rodriguez@email.com',
      country: 'Spain',
      industry: 'Marketing',
      registrationDate: '2024-01-12',
      lastAccess: '2024-01-21',
      totalViews: 28,
      episodeViews: { 1: 10, 2: 8, 3: 10, 4: 6, 5: 9, 6: 5 }
    },
    {
      id: 4,
      name: 'David Wilson',
      email: 'david.wilson@email.com',
      country: 'United Kingdom',
      industry: 'Consulting',
      registrationDate: '2024-01-08',
      lastAccess: '2024-01-18',
      totalViews: 55,
      episodeViews: { 1: 20, 2: 22, 3: 13, 4: 15, 5: 18, 6: 12 }
    },
    {
      id: 5,
      name: 'Lisa Zhang',
      email: 'lisa.zhang@email.com',
      country: 'Australia',
      industry: 'Healthcare',
      registrationDate: '2024-01-14',
      lastAccess: '2024-01-20',
      totalViews: 38,
      episodeViews: { 1: 15, 2: 12, 3: 11, 4: 9, 5: 10, 6: 8 }
    }
  ]);

  // Episode editing state
  const [editingEpisode, setEditingEpisode] = useState<Episode | null>(null);
  const [isEditingEpisode, setIsEditingEpisode] = useState(false);

  // Episode creation form state
  const [newEpisode, setNewEpisode] = useState({
    title: '',
    description: '',
    guest: '',
    guestLinkedin: '',
    guest2: '',
    guestLinkedin2: '',
    guest3: '',
    guestLinkedin3: '',
    guest4: '',
    guestLinkedin4: '',
    guest5: '',
    guestLinkedin5: '',
    notes: '',
    showNotes: '',
    transcript: '',
    resource1Name: '',
    resource1Url: '',
    resource2Name: '',
    resource2Url: '',
    resource3Name: '',
    resource3Url: '',
    relatedEpisodes: [] as number[],
    category: '',
    category2: '',
    category3: '',
    category4: '',
    category5: '',
    audioUrl: '',
    videoUrl: '',
    mediaType: 'audio' as 'audio' | 'video',
    duration: '',
    advertisingImage: '',
    spotify_url: '',
    youtube_url: ''
  });
  const [isSubmittingEpisode, setIsSubmittingEpisode] = useState(false);
  const [episodeSubmitStatus, setEpisodeSubmitStatus] = useState('');

  // Category management state
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  // Industry management state
  const [newIndustry, setNewIndustry] = useState({ name: '', description: '' });
  const [editingIndustry, setEditingIndustry] = useState<any>(null);
  const [isAddingIndustry, setIsAddingIndustry] = useState(false);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      loginData.username === 'AdminPDPAS' &&
      loginData.password ===
        'odsl#jd+Zaksk@skdsiijFksji49d9xmmchdcdosdjoej4i1hdgs0dFhvu'
    ) {
      setIsAuthenticated(true);
      setLoginError('');
    } else {
      setLoginError('Invalid credentials');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setLoginData({ username: '', password: '' });
  };

  // Category management functions
  const handleAddCategory = async () => {
    if (newCategory.name.trim()) {
      try {
        await createCategory({
          name: newCategory.name.trim(),
          description: newCategory.description.trim()
        });
        setNewCategory({ name: '', description: '' });
        setIsAddingCategory(false);
      } catch (error) {
        console.error('Failed to create category:', error);
      }
    }
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
  };

  const handleUpdateCategory = async () => {
    if (editingCategory) {
      try {
        await updateCategory(editingCategory.id, {
          name: editingCategory.name,
          description: editingCategory.description
        });
        setEditingCategory(null);
      } catch (error) {
        console.error('Failed to update category:', error);
      }
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      try {
        await deleteCategory(categoryId);
      } catch (error) {
        console.error('Failed to delete category:', error);
      }
    }
  };

  // Industry management functions
  const handleAddIndustry = async () => {
    if (newIndustry.name.trim()) {
      try {
        await createIndustry({
          name: newIndustry.name.trim(),
          description: newIndustry.description.trim()
        });
        setNewIndustry({ name: '', description: '' });
        setIsAddingIndustry(false);
      } catch (error) {
        console.error('Failed to create industry:', error);
      }
    }
  };

  const handleEditIndustry = (industry: any) => {
    setEditingIndustry(industry);
  };

  const handleUpdateIndustry = async () => {
    if (editingIndustry) {
      try {
        await updateIndustry(editingIndustry.id, {
          name: editingIndustry.name,
          description: editingIndustry.description
        });
        setEditingIndustry(null);
      } catch (error) {
        console.error('Failed to update industry:', error);
      }
    }
  };

  const handleDeleteIndustry = async (industryId: number) => {
    if (confirm('Are you sure you want to delete this industry? This action cannot be undone.')) {
      try {
        await deleteIndustry(industryId);
      } catch (error) {
        console.error('Failed to delete industry:', error);
      }
    }
  };

  // Episode editing functions
  const handleEditEpisode = (episode: Episode) => {
    setEditingEpisode({
      ...episode,
      // Ensure all fields are properly mapped
      guest2: episode.guest2 || '',
      guestLinkedin2: episode.guestLinkedin2 || '',
      guest3: episode.guest3 || '',
      guestLinkedin3: episode.guestLinkedin3 || '',
      guest4: episode.guest4 || '',
      guestLinkedin4: episode.guestLinkedin4 || '',
      guest5: episode.guest5 || '',
      guestLinkedin5: episode.guestLinkedin5 || '',
      notes: episode.notes || '',
      showNotes: episode.showNotes || '',
      transcript: episode.transcript || '',
      resource1Name: episode.resource1Name || '',
      resource1Url: episode.resource1Url || '',
      resource2Name: episode.resource2Name || '',
      resource2Url: episode.resource2Url || '',
      resource3Name: episode.resource3Name || '',
      resource3Url: episode.resource3Url || '',
      relatedEpisodes: episode.relatedEpisodes || [],
      category2: episode.category2 || '',
      category3: episode.category3 || '',
      category4: episode.category4 || '',
      category5: episode.category5 || '',
      duration: episode.duration || '',
      advertisingImage: episode.advertisingImage || '',
      spotify_url: episode.spotify_url || '',
      youtube_url: episode.youtube_url || ''
    });
    setIsEditingEpisode(true);
    setActiveTab('create-episode'); // Switch to the form tab
  };

  const handleSaveEpisode = async () => {
    if (editingEpisode && editingEpisode.id) {
      try {
        setIsSubmittingEpisode(true);
        await updateEpisode(editingEpisode.id, editingEpisode);
        setEpisodeSubmitStatus('Episode updated successfully!');
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } catch (error) {
        console.error('Failed to update episode:', error);
        setEpisodeSubmitStatus('Failed to update episode. Please try again.');
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } finally {
        setIsSubmittingEpisode(false);
      }
    }
  };

  // URL validation and platform detection functions
  const validatePlatformUrl = (url: string, platform: 'spotify' | 'youtube') => {
    if (!url.trim()) return { isValid: true, error: '' };
    
    const patterns = {
      spotify: /^https?:\/\/(open\.spotify\.com\/(episode|show|track)\/[a-zA-Z0-9]+|spotify\.link\/[a-zA-Z0-9]+)/,
      youtube: /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)[a-zA-Z0-9_-]+/
    };
    
    const isValid = patterns[platform].test(url);
    return {
      isValid,
      error: isValid ? '' : `Please enter a valid ${platform === 'spotify' ? 'Spotify' : 'YouTube'} URL`
    };
  };

  const detectPlatformFromUrl = (url: string) => {
    if (!url.trim()) return null;
    
    if (url.includes('spotify.com') || url.includes('spotify.link')) return 'spotify';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    return null;
  };

  const getPreviewData = async (url: string, platform: 'spotify' | 'youtube') => {
    // In a real implementation, you would fetch metadata from the platforms
    // For now, we'll return basic preview info based on URL patterns
    try {
      if (platform === 'spotify') {
        const episodeId = url.match(/episode\/([a-zA-Z0-9]+)/)?.[1];
        if (episodeId) {
          return {
            title: 'Spotify Episode',
            thumbnail: 'https://developer.spotify.com/assets/branding-guidelines/icon1@2x.png',
            platform: 'Spotify'
          };
        }
      } else if (platform === 'youtube') {
        const videoId = url.match(/(?:watch\?v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]+)/)?.[1];
        if (videoId) {
          return {
            title: 'YouTube Video',
            thumbnail: `https://img.youtube.com/vi/${videoId}/default.jpg`,
            platform: 'YouTube'
          };
        }
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
    }
    return null;
  };

  const [urlValidation, setUrlValidation] = useState({
    spotify: { isValid: true, error: '', preview: null as any },
    youtube: { isValid: true, error: '', preview: null as any }
  });

  const handlePlatformUrlChange = async (platform: 'spotify' | 'youtube', url: string) => {
    // Update the episode data
    if (isEditingEpisode) {
      setEditingEpisode(prev => prev ? { ...prev, [`${platform}_url`]: url } : null);
    } else {
      setNewEpisode(prev => ({ ...prev, [`${platform}_url`]: url }));
    }

    // Validate URL
    const validation = validatePlatformUrl(url, platform);
    
    // Get preview if valid
    let preview = null;
    if (validation.isValid && url.trim()) {
      preview = await getPreviewData(url, platform);
    }

    // Auto-detect platform and suggest if URL doesn't match current field
    const detectedPlatform = detectPlatformFromUrl(url);
    if (detectedPlatform && detectedPlatform !== platform && url.trim()) {
      validation.error = `This looks like a ${detectedPlatform === 'spotify' ? 'Spotify' : 'YouTube'} URL. Consider using the ${detectedPlatform === 'spotify' ? 'Spotify' : 'YouTube'} field instead.`;
    }

    setUrlValidation(prev => ({
      ...prev,
      [platform]: { ...validation, preview }
    }));
  };

  // NEW FUNCTIONS FOR THE THREE BUTTONS
  const handleSaveAsDraft = async () => {
    if (isEditingEpisode && editingEpisode && editingEpisode.id) {
      try {
        setIsSubmittingEpisode(true);
        await updateEpisode(editingEpisode.id, {
          ...editingEpisode,
          status: 'Draft',
          visible: false
        });
        setEpisodeSubmitStatus('Episode saved as draft successfully!');
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } catch (error) {
        console.error('Failed to save episode as draft:', error);
        setEpisodeSubmitStatus('Failed to save episode as draft. Please try again.');
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } finally {
        setIsSubmittingEpisode(false);
      }
    } else {
      // For new episodes, create as draft
      try {
        setIsSubmittingEpisode(true);
        await createEpisode({
          title: newEpisode.title,
          description: newEpisode.description,
          guest: newEpisode.guest,
          guestLinkedin: newEpisode.guestLinkedin,
          guest2: newEpisode.guest2,
          guestLinkedin2: newEpisode.guestLinkedin2,
          guest3: newEpisode.guest3,
          guestLinkedin3: newEpisode.guestLinkedin3,
          guest4: newEpisode.guest4,
          guestLinkedn4: '', // Fixed typo - was guestLinkedn4
          guest5: newEpisode.guest5,
          guestLinkedin5: newEpisode.guestLinkedin5,
          notes: newEpisode.notes,
          showNotes: newEpisode.showNotes,
          transcript: newEpisode.transcript,
          resource1Name: newEpisode.resource1Name,
          resource1Url: newEpisode.resource1Url,
          resource2Name: newEpisode.resource2Name,
          resource2Url: newEpisode.resource2Url,
          resource3Name: newEpisode.resource3Name,
          resource3Url: newEpisode.resource3Url,
          relatedEpisodes: newEpisode.relatedEpisodes,
          category: newEpisode.category,
          category2: newEpisode.category2,
          category3: newEpisode.category3,
          category4: newEpisode.category4,
          category5: newEpisode.category5,
          audioUrl: newEpisode.audioUrl,
          videoUrl: newEpisode.videoUrl,
          mediaType: newEpisode.mediaType,
          duration: newEpisode.duration,
          advertisingImage: newEpisode.advertisingImage,
          spotify_url: newEpisode.spotify_url,
          youtube_url: newEpisode.youtube_url,
          date: new Date().toISOString().split('T')[0],
          status: 'Draft',
          visible: false
        });

        setEpisodeSubmitStatus('Episode saved as draft successfully!');
        setNewEpisode({
          title: '',
          description: '',
          guest: '',
          guestLinkedin: '',
          guest2: '',
          guestLinkedin2: '',
          guest3: '',
          guestLinkedin3: '', // Fixed typo - was guestLinkedn3
          guest4: '',
          guestLinkedin4: '', // Fixed typo - was guestLinkedn4
          guest5: '',
          guestLinkedin5: '',
          notes: '',
          showNotes: '',
          transcript: '',
          resource1Name: '',
          resource1Url: '',
          resource2Name: '',
          resource2Url: '',
          resource3Name: '',
          resource3Url: '',
          relatedEpisodes: [],
          category: '',
          category2: '',
          category3: '',
          category4: '',
          category5: '',
          audioUrl: '',
          videoUrl: '',
          mediaType: 'audio' as 'audio' | 'video',
          duration: '',
          advertisingImage: '',
          spotify_url: '',
          youtube_url: ''
        });
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } catch (error) {
        console.error('Failed to save episode as draft:', error);
        setEpisodeSubmitStatus('Failed to save episode as draft. Please try again.');
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } finally {
        setIsSubmittingEpisode(false);
      }
    }
  };

  const handlePublishEpisode = async () => {
    if (isEditingEpisode && editingEpisode && editingEpisode.id) {
      try {
        setIsSubmittingEpisode(true);
        await updateEpisode(editingEpisode.id, {
          ...editingEpisode,
          status: 'Published',
          visible: true
        });
        setEpisodeSubmitStatus('Episode published successfully!');
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } catch (error) {
        console.error('Failed to publish episode:', error);
        setEpisodeSubmitStatus('Failed to publish episode. Please try again.');
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } finally {
        setIsSubmittingEpisode(false);
      }
    } else {
      // For new episodes, create as published
      try {
        setIsSubmittingEpisode(true);
        await createEpisode({
          title: newEpisode.title,
          description: newEpisode.description,
          guest: newEpisode.guest,
          guestLinkedin: newEpisode.guestLinkedin,
          guest2: newEpisode.guest2,
          guestLinkedin2: newEpisode.guestLinkedin2,
          guest3: newEpisode.guest3,
          guestLinkedin3: newEpisode.guestLinkedin3,
          guest4: newEpisode.guest4,
          guestLinkedin4: newEpisode.guestLinkedin4,
          guest5: newEpisode.guest5,
          guestLinkedin5: newEpisode.guestLinkedin5,
          notes: newEpisode.notes,
          showNotes: newEpisode.showNotes,
          transcript: newEpisode.transcript,
          resource1Name: newEpisode.resource1Name,
          resource1Url: newEpisode.resource1Url,
          resource2Name: newEpisode.resource2Name,
          resource2Url: newEpisode.resource2Url,
          resource3Name: newEpisode.resource3Name,
          resource3Url: newEpisode.resource3Url,
          relatedEpisodes: newEpisode.relatedEpisodes,
          category: newEpisode.category,
          category2: newEpisode.category2,
          category3: newEpisode.category3,
          category4: newEpisode.category4,
          category5: newEpisode.category5,
          audioUrl: newEpisode.audioUrl,
          videoUrl: newEpisode.videoUrl,
          mediaType: newEpisode.mediaType,
          duration: newEpisode.duration,
          advertisingImage: newEpisode.advertisingImage,
          spotify_url: newEpisode.spotify_url,
          youtube_url: newEpisode.youtube_url,
          date: new Date().toISOString().split('T')[0],
          status: 'Published',
          visible: true
        });

        setEpisodeSubmitStatus('Episode published successfully!');
        setNewEpisode({
          title: '',
          description: '',
          guest: '',
          guestLinkedin: '',
          guest2: '',
          guestLinkedin2: '',
          guest3: '',
          guestLinkedin3: '',
          guest4: '',
          guestLinkedin4: '',
          guest5: '',
          guestLinkedin5: '',
          notes: '',
          showNotes: '',
          transcript: '',
          resource1Name: '',
          resource1Url: '',
          resource2Name: '',
          resource2Url: '',
          resource3Name: '',
          resource3Url: '',
          relatedEpisodes: [],
          category: '',
          category2: '',
          category3: '',
          category4: '',
          category5: '',
          audioUrl: '',
          videoUrl: '',
          mediaType: 'audio' as 'audio' | 'video',
          duration: '',
          advertisingImage: '',
          spotify_url: '',
          youtube_url: ''
        });
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } catch (error) {
        console.error('Failed to publish episode:', error);
        setEpisodeSubmitStatus('Failed to publish episode. Please try again.');
        setTimeout(() => setEpisodeSubmitStatus(''), 3000);
      } finally {
        setIsSubmittingEpisode(false);
      }
    }
  };

  const handleEraseEpisode = async () => {
    if (isEditingEpisode && editingEpisode && editingEpisode.id) {
      if (confirm('Are you sure you want to permanently delete this episode? This action cannot be undone.')) {
        try {
          setIsSubmittingEpisode(true);
          // Note: You'll need to implement deleteEpisode in your useEpisodes hook
          // For now, we'll use updateEpisode to mark it as deleted or handle accordingly
          await updateEpisode(editingEpisode.id, {
            ...editingEpisode,
            visible: false,
            status: 'Deleted'
          });
          setEpisodeSubmitStatus('Episode deleted successfully!');
          setTimeout(() => {
            setEpisodeSubmitStatus('');
            setIsEditingEpisode(false);
            setEditingEpisode(null);
            setActiveTab('episodes');
          }, 2000);
        } catch (error) {
          console.error('Failed to delete episode:', error);
          setEpisodeSubmitStatus('Failed to delete episode. Please try again.');
          setTimeout(() => setEpisodeSubmitStatus(''), 3000);
        } finally {
          setIsSubmittingEpisode(false);
        }
      }
    }
  };

  const handleCreateNewEpisode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingEpisode(true);
    setEpisodeSubmitStatus('');

    try {
      await createEpisode({
        title: newEpisode.title,
        description: newEpisode.description,
        guest: newEpisode.guest,
        guestLinkedin: newEpisode.guestLinkedin,
        guest2: newEpisode.guest2,
        guestLinkedin2: newEpisode.guestLinkedin2,
        guest3: newEpisode.guest3,
        guestLinkedin3: newEpisode.guestLinkedin3,
        guest4: newEpisode.guest4,
        guestLinkedin4: newEpisode.guestLinkedin4,
        guest5: newEpisode.guest5,
        guestLinkedin5: newEpisode.guestLinkedin5,
        notes: newEpisode.notes,
        showNotes: newEpisode.showNotes,
        transcript: newEpisode.transcript,
        resource1Name: newEpisode.resource1Name,
        resource1Url: newEpisode.resource1Url,
        resource2Name: newEpisode.resource2Name,
        resource2Url: newEpisode.resource2Url,
        resource3Name: newEpisode.resource3Name,
        resource3Url: newEpisode.resource3Url,
        relatedEpisodes: newEpisode.relatedEpisodes,
        category: newEpisode.category,
        category2: newEpisode.category2,
        category3: newEpisode.category3,
        category4: newEpisode.category4,
        category5: newEpisode.category5,
        audioUrl: newEpisode.audioUrl,
        videoUrl: newEpisode.videoUrl,
        mediaType: newEpisode.mediaType,
        duration: newEpisode.duration,
        advertisingImage: newEpisode.advertisingImage,
        spotify_url: newEpisode.spotify_url,
        youtube_url: newEpisode.youtube_url,
        date: new Date().toISOString().split('T')[0]
      });

      setEpisodeSubmitStatus('Episode created successfully!');
      setNewEpisode({
        title: '',
        description: '',
        guest: '',
        guestLinkedin: '',
        guest2: '',
        guestLinkedin2: '',
        guest3: '',
        guestLinkedin3: '',
        guest4: '',
        guestLinkedin4: '',
        guest5: '',
        guestLinkedin5: '',
        notes: '',
        showNotes: '',
        transcript: '',
        resource1Name: '',
        resource1Url: '',
        resource2Name: '',
        resource2Url: '',
        resource3Name: '',
        resource3Url: '',
        relatedEpisodes: [],
        category: '',
        category2: '',
        category3: '',
        category4: '',
        category5: '',
        audioUrl: '',
        videoUrl: '',
        mediaType: 'audio' as 'audio' | 'video',
        duration: '',
        advertisingImage: '',
        spotify_url: '',
        youtube_url: ''
      });
    } catch (error) {
      console.error('Failed to create episode:', error);
      setEpisodeSubmitStatus('Failed to create episode. Please try again.');
    } finally {
      setIsSubmittingEpisode(false);
    }
  };

  const filteredSubscribers = subscribers.filter((subscriber) => {
    const matchesSearch =
      subscriber.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.industry.toLowerCase().includes(searchTerm.toLowerCase());

    if (selectedFilter === 'all') return matchesSearch;
    if (selectedFilter === 'recent') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return matchesSearch && new Date(subscriber.lastAccess) > weekAgo;
    }
    if (selectedFilter === 'active') {
      return matchesSearch && subscriber.totalViews > 20;
    }
    return matchesSearch;
  });

  const totalSubscribers = subscribers.length;
  const totalViews = subscribers.reduce((sum, sub) => sum + sub.totalViews, 0);
  const averageViews = Math.round(totalViews / totalSubscribers);
  const recentSubscribers = subscribers.filter((sub) => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return new Date(sub.registrationDate) > weekAgo;
  }).length;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-gray-50">
        <SEOHead
          title="Admin Login | DBD Podcast"
          description="Admin access for DBD Podcast management dashboard."
          noindex={true}
        />
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-admin-line text-white text-2xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-800">Admin Login</h1>
              <p className="text-gray-600">Access subscriber management dashboard</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  value={loginData.username}
                  onChange={(e) => setLoginData((prev) => ({ ...prev, username: e.target.value }))}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter username"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData((prev) => ({ ...prev, password: e.target.value }))}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter password"
                />
              </div>

              {loginError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <p className="text-red-700 text-sm">{loginError}</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Login
              </button>
            </form>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 text-center">Access the admin panel at /admin</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      <SEOHead title="Admin Dashboard | DBD Podcast" description="Admin dashboard for DBD Podcast management." noindex={true} />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
              <p className="text-gray-600">Subscriber management and episode creation</p>
            </div>
            <button
              onClick={handleLogout}
              className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-logout-circle-line mr-2"></i>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <section className="py-4">
        <div className="container mx-auto px-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
              {[
                { key: 'subscribers', label: 'Subscribers', icon: 'ri-user-line' },
                { key: 'episodes', label: 'Episodes', icon: 'ri-mic-line' },
                { key: 'create-episode', label: 'Create Episode', icon: 'ri-add-line' },
                { key: 'categories', label: 'Categories', icon: 'ri-folder-line' },
                { key: 'industries', label: 'Industries', icon: 'ri-building-line' },
                { key: 'data', label: 'Data Management', icon: 'ri-database-line' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key ? 'bg-amber-500 text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Data Management Tab */}
      {activeTab === 'data' && (
        <section className="py-8">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <DataExportImport />
            </div>
          </div>
        </section>
      )}

      {/* Subscribers Tab */}
      {activeTab === 'subscribers' && (
        <>
          {/* Stats */}
          <section className="py-8">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-4 gap-6">
                <StatCard
                  title="Total Subscribers"
                  value={totalSubscribers}
                  icon="ri-user-line"
                  bgColor="bg-blue-100"
                  iconColor="text-blue-600"
                />
                <StatCard
                  title="Total Views"
                  value={totalViews}
                  icon="ri-eye-line"
                  bgColor="bg-green-100"
                  iconColor="text-green-600"
                />
                <StatCard
                  title="Average Views"
                  value={averageViews}
                  icon="ri-bar-chart-line"
                  bgColor="bg-amber-100"
                  iconColor="text-amber-600"
                />
                <StatCard
                  title="New This Week"
                  value={recentSubscribers}
                  icon="ri-user-add-line"
                  bgColor="bg-purple-100"
                  iconColor="text-purple-600"
                />
              </div>
            </div>
          </section>

          {/* Filters and Search */}
          <section className="py-4">
            <div className="container mx-auto px-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex flex-wrap gap-2">
                    {['all', 'recent', 'active'].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setSelectedFilter(filter)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                          selectedFilter === filter
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {filter === 'all' && 'All Subscribers'}
                        {filter === 'recent' && 'Recent Activity'}
                        {filter === 'active' && 'High Engagement'}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search subscribers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 text-sm"
                      />
                      <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Subscribers Table */}
          <section className="py-8">
            <div className="container mx-auto px-6">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Subscriber', 'Country', 'Industry', 'Registration', 'Last Access', 'Total Views', 'Episode Views'].map(
                          (head) => (
                            <th
                              key={head}
                              className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              {head}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredSubscribers.map((subscriber) => (
                        <tr key={subscriber.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{subscriber.name}</div>
                              <div className="text-sm text-gray-500">{subscriber.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscriber.country}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscriber.industry || 'Not specified'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(subscriber.registrationDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(subscriber.lastAccess).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {subscriber.totalViews} views
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {episodes.map((episode) => (
                                <span
                                  key={episode.id}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                  title={episode.title}
                                >
                                  E{episode.id}: {subscriber.episodeViews[episode.id] || 0}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {filteredSubscribers.length === 0 && (
                  <div className="text-center py-12">
                    <i className="ri-user-search-line text-4xl text-gray-300 mb-4"></i>
                    <h3 className="text-lg font-medium text-gray-600 mb-2">No subscribers found</h3>
                    <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Episode Analytics */}
          <section className="py-8">
            <div className="container mx-auto px-6">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Episode Performance</h2>
                {episodesLoading ? (
                  <div className="text-center py-8">
                    <i className="ri-loader-4-line animate-spin text-4xl text-gray-300 mb-4"></i>
                    <p className="text-gray-600">Loading episodes...</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {episodes.map((episode) => {
                      const totalViews = subscribers.reduce(
                        (sum, sub) => sum + (sub.episodeViews[episode.id] || 0),
                        0
                      );
                      return (
                        <div key={episode.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-all duration-200">
                          <div className="mb-3">
                            {episode.advertisingImage && (
                              <img
                                src={episode.advertisingImage}
                                alt={episode.title}
                                className="w-full h-32 object-cover rounded-lg mb-3"
                              />
                            )}
                          </div>
                          <button
                            onClick={() => handleEditEpisode(episode)}
                            className="font-medium text-gray-800 mb-2 line-clamp-2 hover:text-amber-600 transition-colors cursor-pointer text-left w-full"
                          >
                            {episode.title}
                          </button>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Episode {episode.id}</span>
                            <span className="text-lg font-bold text-amber-600">{episode.viewCount || totalViews} views</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Episodes Management Tab */}
      {activeTab === 'episodes' && (
        <section className="py-8">
          <div className="container mx-auto px-6">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Episodes Management</h2>
                    <p className="text-gray-600">Manage all episodes, their visibility, and focus status</p>
                  </div>
                  <button
                    onClick={() => setActiveTab('create-episode')}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Create Episode
                  </button>
                </div>

                {episodesLoading ? (
                  <div className="text-center py-8">
                    <i className="ri-loader-4-line animate-spin text-4xl text-gray-300 mb-4"></i>
                    <p className="text-gray-600">Loading episodes...</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            {['Nr of Episode', 'Episode Title', 'Date Published', 'Status', 'Views', 'On Focus', 'Visible/Not'].map(
                              (head) => (
                                <th
                                  key={head}
                                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  {head}
                                </th>
                              )
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {episodes.map((episode) => (
                            <tr key={episode.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {episode.id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => handleEditEpisode(episode)}
                                  className="text-sm font-medium text-amber-600 hover:text-amber-800 transition-colors cursor-pointer text-left line-clamp-2 max-w-xs"
                                  title={episode.title}
                                >
                                  {episode.title}
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(episode.date).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <select
                                  value={episode.status || 'Draft'}
                                  onChange={async (e) => {
                                    try {
                                      await updateEpisode(episode.id!, {
                                        status: e.target.value as 'Published' | 'Draft'
                                      });
                                    } catch (error) {
                                      console.error('Failed to update status:', error);
                                    }
                                  }}
                                  className="text-sm border border-gray-300 rounded px-2 py-1 pr-8 cursor-pointer focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                >
                                  <option value="Published">Published</option>
                                  <option value="Draft">Draft</option>
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {episode.viewCount || 0} views
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="radio"
                                  name="onFocus"
                                  checked={episode.on_focus || false}
                                  onChange={async () => {
                                    try {
                                      // First, set all episodes to not be on focus
                                      for (const ep of episodes) {
                                        if (ep.id !== episode.id && ep.on_focus) {
                                          await updateEpisode(ep.id!, { on_focus: false });
                                        }
                                      }
                                      // Then set this episode as on focus
                                      await updateEpisode(episode.id!, { on_focus: true });
                                    } catch (error) {
                                      console.error('Failed to update focus:', error);
                                    }
                                  }}
                                  className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 focus:ring-amber-500 cursor-pointer"
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <select
                                  value={episode.visible ? 'visible' : 'not-visible'}
                                  onChange={async (e) => {
                                    try {
                                      await updateEpisode(episode.id!, {
                                        visible: e.target.value === 'visible'
                                      });
                                    } catch (error) {
                                      console.error('Failed to update visibility:', error);
                                    }
                                  }}
                                  className="text-sm border border-gray-300 rounded px-2 py-1 pr-8 cursor-pointer focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                >
                                  <option value="visible">Visible</option>
                                  <option value="not-visible">Not Visible</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {episodes.length === 0 && (
                      <div className="text-center py-12">
                        <i className="ri-mic-line text-4xl text-gray-300 mb-4"></i>
                        <h3 className="text-lg font-medium text-gray-600 mb-2">No episodes yet</h3>
                        <p className="text-gray-500">Create your first episode to get started</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Episode Creation/Edit Form Tab */}
      {activeTab === 'create-episode' && (
        <section className="py-8">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                      {isEditingEpisode ? 'Edit Episode' : 'Create New Episode'}
                    </h2>
                    <p className="text-gray-600">
                      {isEditingEpisode ? 'Update episode information and content' : 'Add a new episode to your podcast'}
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    {isEditingEpisode && (
                      <button
                        onClick={() => {
                          setIsEditingEpisode(false);
                          setEditingEpisode(null);
                          setNewEpisode({
                            title: '',
                            description: '',
                            guest: '',
                            guestLinkedin: '',
                            guest2: '',
                            guestLinkedin2: '',
                            guest3: '',
                            guestLinkedin3: '',
                            guest4: '',
                            guestLinkedin4: '',
                            guest5: '',
                            guestLinkedin5: '',
                            notes: '',
                            showNotes: '',
                            transcript: '',
                            resource1Name: '',
                            resource1Url: '',
                            resource2Name: '',
                            resource2Url: '',
                            resource3Name: '',
                            resource3Url: '',
                            relatedEpisodes: [],
                            category: '',
                            category2: '',
                            category3: '',
                            category4: '',
                            category5: '',
                            audioUrl: '',
                            videoUrl: '',
                            mediaType: 'audio' as 'audio' | 'video',
                            duration: '',
                            advertisingImage: '',
                            spotify_url: '',
                            youtube_url: ''
                          });
                        }}
                        className="bg-green-500 hover:bg-green-600 text-white font-medium py-1 px-3 rounded text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-add-line mr-2"></i>
                        Create New Episode
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setActiveTab('episodes');
                        setIsEditingEpisode(false);
                        setEditingEpisode(null);
                      }}
                      className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-1 px-3 rounded text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      Back to Episodes
                    </button>
                  </div>
                </div>

                {episodeSubmitStatus && (
                  <div className={`mb-6 p-4 rounded-lg ${
                    episodeSubmitStatus.includes('successfully') 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : episodeSubmitStatus.includes('Failed')
                      ? 'bg-red-50 border border-red-200 text-red-700'
                      : 'bg-blue-50 border border-blue-200 text-blue-700'
                  }`}>
                    <p className="text-sm font-medium">{episodeSubmitStatus}</p>
                  </div>
                )}

                <form 
                  onSubmit={isEditingEpisode ? (e) => { e.preventDefault(); handleSaveEpisode(); } : handleCreateNewEpisode} 
                  className="space-y-8"
                >
                  {/* Basic Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Episode Title *</label>
                      <input
                        type="text"
                        value={isEditingEpisode ? editingEpisode?.title || '' : newEpisode.title}
                        onChange={(e) => {
                          if (isEditingEpisode) {
                            setEditingEpisode(prev => prev ? { ...prev, title: e.target.value } : null);
                          } else {
                            setNewEpisode(prev => ({ ...prev, title: e.target.value }));
                          }
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="Enter episode title"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Duration</label>
                      <input
                        type="text"
                        value={isEditingEpisode ? editingEpisode?.duration || '' : newEpisode.duration}
                        onChange={(e) => {
                          if (isEditingEpisode) {
                            setEditingEpisode(prev => prev ? { ...prev, duration: e.target.value } : null);
                          } else {
                            setNewEpisode(prev => ({ ...prev, duration: e.target.value }));
                          }
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="e.g., 45 min"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
                    <textarea
                      rows={4}
                      value={isEditingEpisode ? editingEpisode?.description || '' : newEpisode.description}
                      onChange={(e) => {
                        if (isEditingEpisode) {
                          setEditingEpisode(prev => prev ? { ...prev, description: e.target.value } : null);
                        } else {
                          setNewEpisode(prev => ({ ...prev, description: e.target.value }));
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Describe what this episode is about..."
                      required
                    />
                  </div>

                  {/* Guest Information - Complete with all 5 guests */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Guest Information</h3>
                    <div className="space-y-6">
                      {/* Guest 1 */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Primary Guest *</label>
                          <input
                            type="text"
                            value={isEditingEpisode ? editingEpisode?.guest || '' : newEpisode.guest}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guest: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guest: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Guest name and title"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Guest LinkedIn</label>
                          <input
                            type="url"
                            value={isEditingEpisode ? editingEpisode?.guestLinkedin || '' : newEpisode.guestLinkedin}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guestLinkedin: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guestLinkedin: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="https://linkedin.com/in/guest"
                          />
                        </div>
                      </div>

                      {/* Guest 2 */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Guest 2</label>
                          <input
                            type="text"
                            value={isEditingEpisode ? editingEpisode?.guest2 || '' : newEpisode.guest2}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guest2: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guest2: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Second guest name and title"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Guest 2 LinkedIn</label>
                          <input
                            type="url"
                            value={isEditingEpisode ? editingEpisode?.guestLinkedin2 || '' : newEpisode.guestLinkedin2}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guestLinkedin2: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guestLinkedin2: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="https://linkedin.com/in/guest2"
                          />
                        </div>
                      </div>

                      {/* Guest 3 */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Guest 3</label>
                          <input
                            type="text"
                            value={isEditingEpisode ? editingEpisode?.guest3 || '' : newEpisode.guest3}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guest3: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guest3: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Third guest name and title"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Guest 3 LinkedIn</label>
                          <input
                            type="url"
                            value={isEditingEpisode ? editingEpisode?.guestLinkedin3 || '' : newEpisode.guestLinkedin3}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guestLinkedin3: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guestLinkedin3: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="https://linkedin.com/in/guest3"
                          />
                        </div>
                      </div>

                      {/* Guest 4 */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Guest 4</label>
                          <input
                            type="text"
                            value={isEditingEpisode ? editingEpisode?.guest4 || '' : newEpisode.guest4}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guest4: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guest4: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Fourth guest name and title"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Guest 4 LinkedIn</label>
                          <input
                            type="url"
                            value={isEditingEpisode ? editingEpisode?.guestLinkedin4 || '' : newEpisode.guestLinkedin4}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guestLinkedin4: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guestLinkedin4: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="https://linkedin.com/in/guest4"
                          />
                        </div>
                      </div>

                      {/* Guest 5 */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Guest 5</label>
                          <input
                            type="text"
                            value={isEditingEpisode ? editingEpisode?.guest5 || '' : newEpisode.guest5}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guest5: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guest5: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Fifth guest name and title"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Guest 5 LinkedIn</label>
                          <input
                            type="url"
                            value={isEditingEpisode ? editingEpisode?.guestLinkedin5 || '' : newEpisode.guestLinkedin5}
                            onChange={(e) => {
                              if (isEditingEpisode) {
                                setEditingEpisode(prev => prev ? { ...prev, guestLinkedin5: e.target.value } : null);
                              } else {
                                setNewEpisode(prev => ({ ...prev, guestLinkedin5: e.target.value }));
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="https://linkedin.com/in/guest5"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Content */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                      <textarea
                        rows={3}
                        value={isEditingEpisode ? editingEpisode?.notes || '' : newEpisode.notes}
                        onChange={(e) => {
                          if (isEditingEpisode) {
                            setEditingEpisode(prev => prev ? { ...prev, notes: e.target.value } : null);
                          } else {
                            setNewEpisode(prev => ({ ...prev, notes: e.target.value }));
                          }
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="Internal notes for this episode"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Show Notes</label>
                      <textarea
                        rows={3}
                        value={isEditingEpisode ? editingEpisode?.showNotes || '' : newEpisode.showNotes}
                        onChange={(e) => {
                          if (isEditingEpisode) {
                            setEditingEpisode(prev => prev ? { ...prev, showNotes: e.target.value } : null);
                          } else {
                            setNewEpisode(prev => ({ ...prev, showNotes: e.target.value }));
                          }
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="Notes displayed to listeners"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Transcript</label>
                    <textarea
                      rows={6}
                      value={isEditingEpisode ? editingEpisode?.transcript || '' : newEpisode.transcript}
                      onChange={(e) => {
                        if (isEditingEpisode) {
                          setEditingEpisode(prev => prev ? { ...prev, transcript: e.target.value } : null);
                        } else {
                          setNewEpisode(prev => ({ ...prev, transcript: e.target.value }));
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Full episode transcript"
                    />
                  </div>

                  {/* Resources */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Resources</h3>
                    <div className="space-y-4">
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Resource {num} Name</label>
                            <input
                              type="text"
                              value={isEditingEpisode ? (editingEpisode as any)[`resource${num}Name`] || '' : (newEpisode as any)[`resource${num}Name`]}
                              onChange={(e) => {
                                if (isEditingEpisode) {
                                  setEditingEpisode(prev => prev ? { ...prev, [`resource${num}Name`]: e.target.value } : null);
                                } else {
                                  setNewEpisode(prev => ({ ...prev, [`resource${num}Name`]: e.target.value }));
                                }
                              }}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                              placeholder={`Resource ${num} name`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Resource {num} URL</label>
                            <input
                              type="url"
                              value={isEditingEpisode ? (editingEpisode as any)[`resource${num}Url`] || '' : (newEpisode as any)[`resource${num}Url`]}
                              onChange={(e) => {
                                if (isEditingEpisode) {
                                  setEditingEpisode(prev => prev ? { ...prev, [`resource${num}Url`]: e.target.value } : null);
                                } else {
                                  setNewEpisode(prev => ({ ...prev, [`resource${num}Url`]: e.target.value }));
                                }
                              }}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                              placeholder={`https://resource${num}.com`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Media URLs */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Spotify URL</label>
                      <input
                        type="url"
                        value={isEditingEpisode ? editingEpisode?.spotify_url || '' : newEpisode.spotify_url}
                        onChange={(e) => handlePlatformUrlChange('spotify', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                          urlValidation.spotify.error ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="https://open.spotify.com/episode/..."
                      />
                      {urlValidation.spotify.preview && (
                        <div className="mt-2 flex items-center text-sm">
                          <img 
                            src={urlValidation.spotify.preview.thumbnail} 
                            alt={urlValidation.spotify.preview.title} 
                            className="w-8 h-8 mr-2 rounded"
                          />
                          <span className="text-gray-600">{urlValidation.spotify.preview.title} ({urlValidation.spotify.preview.platform})</span>
                        </div>
                      )}
                      {urlValidation.spotify.error && (
                        <p className="mt-1 text-sm text-red-600">{urlValidation.spotify.error}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">YouTube URL</label>
                      <input
                        type="url"
                        value={isEditingEpisode ? editingEpisode?.youtube_url || '' : newEpisode.youtube_url}
                        onChange={(e) => handlePlatformUrlChange('youtube', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                          urlValidation.youtube.error ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="https://youtube.com/watch?v=..."
                      />
                      {urlValidation.youtube.preview && (
                        <div className="mt-2 flex items-center text-sm">
                          <img 
                            src={urlValidation.youtube.preview.thumbnail} 
                            alt={urlValidation.youtube.preview.title} 
                            className="w-8 h-8 mr-2 rounded"
                          />
                          <span className="text-gray-600">{urlValidation.youtube.preview.title} ({urlValidation.youtube.preview.platform})</span>
                        </div>
                      )}
                      {urlValidation.youtube.error && (
                        <p className="mt-1 text-sm text-red-600">{urlValidation.youtube.error}</p>
                      )}
                    </div>
                  </div>

                  {/* Categories */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Categories</h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <div key={num}>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Category {num}
                          </label>
                          {num === 1 ? (
                            <select
                              value={isEditingEpisode ? editingEpisode?.category || '' : newEpisode.category}
                              onChange={(e) => {
                                if (isEditingEpisode) {
                                  setEditingEpisode(prev => prev ? { ...prev, category: e.target.value } : null);
                                } else {
                                  setNewEpisode(prev => ({ ...prev, category: e.target.value }));
                                }
                              }}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                              required
                            >
                              <option value="">Select a category</option>
                              {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <select
                              value={isEditingEpisode ? (editingEpisode as any)[`category${num}`] || '' : (newEpisode as any)[`category${num}`]}
                              onChange={(e) => {
                                if (isEditingEpisode) {
                                  setEditingEpisode(prev => prev ? { ...prev, [`category${num}`]: e.target.value } : null);
                                } else {
                                  setNewEpisode(prev => ({ ...prev, [`category${num}`]: e.target.value }));
                                }
                              }}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            >
                              <option value="">Select a category (optional)</option>
                              {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Media Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Audio URL</label>
                      <input
                        type="url"
                        value={isEditingEpisode ? editingEpisode?.audioUrl || '' : newEpisode.audioUrl}
                        onChange={(e) => {
                          if (isEditingEpisode) {
                            setEditingEpisode(prev => prev ? { ...prev, audioUrl: e.target.value } : null);
                          } else {
                            setNewEpisode(prev => ({ ...prev, audioUrl: e.target.value }));
                          }
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="https://audio-host.com/episode.mp3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Video URL</label>
                      <input
                        type="url"
                        value={isEditingEpisode ? editingEpisode?.videoUrl || '' : newEpisode.videoUrl}
                        onChange={(e) => {
                          if (isEditingEpisode) {
                            setEditingEpisode(prev => prev ? { ...prev, videoUrl: e.target.value } : null);
                          } else {
                            setNewEpisode(prev => ({ ...prev, videoUrl: e.target.value }));
                          }
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="https://video-host.com/episode.mp4"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Advertising Image URL</label>
                    <input
                      type="url"
                      value={isEditingEpisode ? editingEpisode?.advertisingImage || '' : newEpisode.advertisingImage}
                      onChange={(e) => {
                        if (isEditingEpisode) {
                          setEditingEpisode(prev => prev ? { ...prev, advertisingImage: e.target.value } : null);
                        } else {
                          setNewEpisode(prev => ({ ...prev, advertisingImage: e.target.value }));
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="https://image-host.com/ad-image.jpg"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-4 pt-6">
                    <button
                      type="submit"
                      disabled={isSubmittingEpisode}
                      className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 flex items-center whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {isSubmittingEpisode ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="ri-save-line mr-2"></i>
                          {isEditingEpisode ? 'Save Changes' : 'Create Episode'}
                        </>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleSaveAsDraft}
                      disabled={isSubmittingEpisode}
                      className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 flex items-center whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {isSubmittingEpisode ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="ri-file-copy-line mr-2"></i>
                          Save as Draft
                        </>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={handlePublishEpisode}
                      disabled={isSubmittingEpisode}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 flex items-center whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {isSubmittingEpisode ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-line mr-2"></i>
                          Publish Episode
                        </>
                      )}
                    </button>
                    
                    {isEditingEpisode && (
                      <button
                        type="button"
                        onClick={handleEraseEpisode}
                        disabled={isSubmittingEpisode}
                        className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 flex items-center whitespace-nowrap cursor-pointer disabled:opacity-50"
                      >
                        {isSubmittingEpisode ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="ri-delete-bin-line mr-2"></i>
                            Delete Episode
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Categories Management Tab */}
      {activeTab === 'categories' && (
        <section className="py-8">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Categories Management</h2>
                    <p className="text-gray-600">Manage podcast categories and topics</p>
                  </div>
                  <button
                    onClick={() => setIsAddingCategory(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Add Category
                  </button>
                </div>

                {/* Add Category Form */}
                {isAddingCategory && (
                  <div className="mb-8 p-6 border border-gray-200 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Category</h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Category Name *</label>
                        <input
                          type="text"
                          value={newCategory.name}
                          onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          placeholder="e.g., Business, Technology, Health"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                        <input
                          type="text"
                          value={newCategory.description}
                          onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          placeholder="Brief description of category"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleAddCategory}
                        className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Save Category
                      </button>
                      <button
                        onClick={() => setIsAddingCategory(false)}
                        className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit Category Form */}
                {editingCategory && (
                  <div className="mb-8 p-6 border border-gray-200 rounded-lg bg-amber-50">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit Category</h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Category Name *</label>
                        <input
                          type="text"
                          value={editingCategory.name}
                          onChange={(e) => setEditingCategory(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                        <input
                          type="text"
                          value={editingCategory.description}
                          onChange={(e) => setEditingCategory(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleUpdateCategory}
                        className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Update Category
                      </button>
                      <button
                        onClick={() => setEditingCategory(null)}
                        className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Categories List */}
                <div className="bg-white border border-gray-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {categories.map((category) => (
                        <tr key={category.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{category.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{category.description || 'No description'}</td>
                          <td className="px-6 py-4 text-right text-sm space-x-2">
                            <button
                              onClick={() => handleEditCategory(category)}
                              className="text-amber-600 hover:text-amber-900 font-medium cursor-pointer"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id!)}
                              className="text-red-600 hover:text-red-900 font-medium cursor-pointer"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {categories.length === 0 && (
                    <div className="text-center py-12">
                      <i className="ri-folder-line text-4xl text-gray-300 mb-4"></i>
                      <h3 className="text-lg font-medium text-gray-600 mb-2">No categories yet</h3>
                      <p className="text-gray-500">Add your first category to get started</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Industries Management Tab */}
      {activeTab === 'industries' && (
        <section className="py-8">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Industries Management</h2>
                    <p className="text-gray-600">Manage subscriber industries</p>
                  </div>
                  <button
                    onClick={() => setIsAddingIndustry(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Add Industry
                  </button>
                </div>

                {/* Add Industry Form */}
                {isAddingIndustry && (
                  <div className="mb-8 p-6 border border-gray-200 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Industry</h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Industry Name *</label>
                        <input
                          type="text"
                          value={newIndustry.name}
                          onChange={(e) => setNewIndustry(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          placeholder="e.g., Technology, Healthcare, Finance"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                        <input
                          type="text"
                          value={newIndustry.description}
                          onChange={(e) => setNewIndustry(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          placeholder="Brief description of industry"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleAddIndustry}
                        className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Save Industry
                      </button>
                      <button
                        onClick={() => setIsAddingIndustry(false)}
                        className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit Industry Form */}
                {editingIndustry && (
                  <div className="mb-8 p-6 border border-gray-200 rounded-lg bg-amber-50">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit Industry</h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Industry Name *</label>
                        <input
                          type="text"
                          value={editingIndustry.name}
                          onChange={(e) => setEditingIndustry(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                        <input
                          type="text"
                          value={editingIndustry.description}
                          onChange={(e) => setEditingIndustry(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleUpdateIndustry}
                        className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Update Industry
                      </button>
                      <button
                        onClick={() => setEditingIndustry(null)}
                        className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Industries List */}
                <div className="bg-white border border-gray-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {industries.map((industry) => (
                        <tr key={industry.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{industry.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{industry.description || 'No description'}</td>
                          <td className="px-6 py-4 text-right text-sm space-x-2">
                            <button
                              onClick={() => handleEditIndustry(industry)}
                              className="text-amber-600 hover:text-amber-900 font-medium cursor-pointer"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteIndustry(industry.id!)}
                              className="text-red-600 hover:text-red-900 font-medium cursor-pointer"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {industries.length === 0 && (
                    <div className="text-center py-12">
                      <i className="ri-building-line text-4xl text-gray-300 mb-4"></i>
                      <h3 className="text-lg font-medium text-gray-600 mb-2">No industries yet</h3>
                      <p className="text-gray-500">Add your first industry to get started</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
